/**
 * Resolves a videoId (which may be a raw ID, a prefixed "vimeo:id" string,
 * or a full URL) into a playable Vimeo video URL.
 */
export const getVimeoUrlFromVideoId = (
  videoId?: string
): string | undefined => {
  if (!videoId) {
    return undefined;
  }

  if (/https?:\/\//.test(videoId)) {
    return videoId;
  }

  const id = `${videoId}`.includes(':') ? videoId.split(':')[1] : videoId;

  return `https://vimeo.com/${id}`;
};

const VIMEO_PLAYER_BASE_URL = 'https://player.vimeo.com/video/';

const getVimeoEmbedParams = (playerId?: string): string =>
  `api=1&player_id=${playerId}&controls=0&title=0&byline=0&portrait=0&muted=1&autopause=0&loop=1`;

/**
 * Builds a Vimeo player embed URL with the appropriate query params
 * for autoplay, muted playback, looping, and postMessage API control.
 */
export const getVimeoEmbedUrl = (url?: string, playerId?: string): string => {
  if (!url) {
    return '';
  }

  if (url.includes(VIMEO_PLAYER_BASE_URL)) {
    const joinChar = url.includes('?') ? '&' : '?';
    return `${url}${joinChar}${getVimeoEmbedParams(playerId)}`;
  }

  const match = `${url}`.match(/vimeo\.com\/(\d+)/);
  if (!match) {
    return '';
  }

  return `${VIMEO_PLAYER_BASE_URL}${match[1]}?${getVimeoEmbedParams(playerId)}`;
};

interface CoverSizeParams {
  containerWidth?: number;
  containerHeight?: number;
  aspectRatio?: number;
}

/**
 * Calculates the width/height needed to cover a container while preserving
 * the video aspect ratio (similar to CSS `object-fit: cover`).
 */
export const getCoverSize = ({
  containerWidth,
  containerHeight,
  aspectRatio,
}: CoverSizeParams): { width: number | string; height: number | string } => {
  if (!containerWidth || !containerHeight || !aspectRatio) {
    return { width: '100%', height: '100%' };
  }

  const containerRatio = containerWidth / containerHeight;

  if (containerRatio > aspectRatio) {
    return {
      width: containerWidth,
      height: containerWidth / aspectRatio,
    };
  }

  return {
    width: containerHeight * aspectRatio,
    height: containerHeight,
  };
};

/**
 * Checks if a given source string is a valid .mp4 video URL.
 */
export const isValidVideoSrc = (src?: string): boolean =>
  typeof src === 'string' && src.toLowerCase().endsWith('.mp4');
